import { useState, useEffect, useRef } from 'react';

// Multilingual inquiry messages - "What are we learning about today?"
export const inquiryMessages = [
  { lang: 'English', text: "What are we learning about today?" },
  { lang: 'Spanish', text: "¿Qué vamos a aprender hoy?" },
  { lang: 'French', text: "Qu'allons-nous apprendre aujourd'hui ?" },
  { lang: 'German', text: "Was lernen wir heute?" },
  { lang: 'Italian', text: "Cosa impareremo oggi?" },
  { lang: 'Portuguese', text: "O que vamos aprender hoje?" },
  { lang: 'Japanese', text: "今日は何を学びますか？" },
  { lang: 'Chinese', text: "我们今天要学什么？" },
  { lang: 'Korean', text: "오늘은 무엇을 배울까요?" },
  { lang: 'Arabic', text: "ماذا سنتعلم اليوم؟" },
  { lang: 'Russian', text: "Что мы будем изучать сегодня?" },
  { lang: 'Hindi', text: "आज हम क्या सीखेंगे?" }
];

export function useInquiryChat({
  onStreamComplete,
  onStreamStart,
  onCharacterTyped
}: {
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  onCharacterTyped?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const lastActivityTimeRef = useRef(Date.now());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasUserActivityRef = useRef(false);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityTimeRef.current = Date.now();
      hasUserActivityRef.current = true;
      
      // Reset to English message if user becomes active and not already on English
      if (currentMessageIndex !== 0) {
        setCurrentMessageIndex(0);
      }
    };

    // Listen for various user activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [currentMessageIndex]);

  // Check for inactivity and cycle messages
  useEffect(() => {
    const checkInactivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;
      
      // Only cycle if user has been inactive for more than 10 seconds
      if (timeSinceLastActivity > 10000) {
        // Start cycling through languages when inactive
        setCurrentMessageIndex((prev) => (prev + 1) % inquiryMessages.length);
        // Keep the inactivity timer running but don't reset it
        // This allows continuous cycling while inactive
      } else if (hasUserActivityRef.current && currentMessageIndex !== 0) {
        // If there's recent activity and we're not on English, go back to English
        setCurrentMessageIndex(0);
      }
    };

    // Check every 2 seconds
    inactivityTimerRef.current = setInterval(checkInactivity, 8000);

    return () => {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [currentMessageIndex]);

  // Stream in the current message
  useEffect(() => {
    const message = inquiryMessages[currentMessageIndex].text;
    
    // Notify that streaming is starting
    if (onStreamStart) onStreamStart();
    
    // Stream in the message
    setDisplayedText('');
    setIsComplete(false);
    let charIndex = 0;

    const streamInInterval = setInterval(() => {
      if (charIndex < message.length) {
        setDisplayedText(message.slice(0, charIndex + 1));
        charIndex++;
        if (onCharacterTyped) onCharacterTyped();
      } else {
        clearInterval(streamInInterval);
        setIsComplete(true);
        if (onStreamComplete) onStreamComplete();
      }
    }, 30);

    return () => {
      clearInterval(streamInInterval);
    };
  }, [currentMessageIndex, onStreamStart, onStreamComplete, onCharacterTyped]);

  return {
    displayedText,
    isComplete,
    message: inquiryMessages[currentMessageIndex].text
  };
}
